/**
 * Text normalisation for spoken input.
 *
 * Recognisers are inconsistent about punctuation, casing and apostrophes, and
 * Hausa makes that worse: the glottal stop in "a’a" or "sha’awa" comes back as
 * a straight quote, a curly quote, a modifier letter, or nothing at all
 * depending on the engine. Everything downstream compares against a single
 * normalised form so those variants stop mattering.
 */

/** All the characters an engine might use for the Hausa glottal stop. */
const APOSTROPHES = /[‘’ʼʻ'`´]/g

/** Hooked letters must survive: ɓ ɗ ƙ ƴ are distinct letters, not decoration. */
const PUNCTUATION = /[.,!?;:()[\]{}"«»…]/g

export function normalize(input: string): string {
  return input
    .normalize('NFC')
    .toLowerCase()
    .replace(APOSTROPHES, "'")
    .replace(PUNCTUATION, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

/** Normalised words, with empties dropped. */
export function words(input: string): string[] {
  const normalized = normalize(input)
  return normalized.length === 0 ? [] : normalized.split(' ')
}

/**
 * Removes a leading phrase if present. Used to strip the conversational
 * wrapper off an answer: "my name is Amina" becomes "Amina".
 */
export function stripLeadIn(input: string, leadIns: readonly string[]): string {
  const normalized = normalize(input)
  let best = ''
  for (const leadIn of leadIns) {
    const candidate = normalize(leadIn)
    if (candidate.length > best.length && normalized.startsWith(candidate + ' ')) {
      best = candidate
    }
  }
  if (best === '') return normalized
  return normalized.slice(best.length + 1).trim()
}

/**
 * Restores display casing for a name or place captured from a recogniser,
 * which usually returns everything lowercase.
 *
 * Hyphens start a new capital. Apostrophes deliberately do not.
 *
 * In Hausa the apostrophe is a glottal stop inside a word, and a large share
 * of personal names carry one: Sa'adu, Rabi'u, Isma'il, Mu'azu. Capitalising
 * after it produced Sa'Adu, stored at high confidence with no read-back, and
 * the typed correction on the profile screen ran back through the same
 * function and mangled it again. There was no way to spell your own name
 * correctly anywhere in the app.
 *
 * The cost is that the conventional Wolof spelling N'Diaye comes back as
 * N'diaye, which the person can fix by typing. Getting a Hausa name wrong with
 * no way to fix it is the worse failure by a wide margin.
 */
export function titleCase(input: string): string {
  return input
    .split(' ')
    .filter(Boolean)
    .map((word) =>
      word
        .split(/([-])/)
        .map((part) =>
          part.length > 0 && /[\p{L}]/u.test(part[0] as string)
            ? (part[0] as string).toUpperCase() + part.slice(1)
            : part,
        )
        .join(''),
    )
    .join(' ')
}

/**
 * Removes a trailing word if it is one of the given set.
 *
 * Hausa puts the equative copula at the end of the clause, so "Ni Amina ce"
 * and "a Kano nake zaune" both carry a grammatical tail that a prefix-stripper
 * cannot reach. Without this, the name is stored as "Amina Ce".
 */
export function stripTrailing(input: string, trailing: readonly string[]): string {
  let tokens = words(input)
  const set = new Set(trailing.map((word) => normalize(word)))
  while (tokens.length > 1) {
    const last = tokens[tokens.length - 1]
    if (last === undefined || !set.has(last)) break
    tokens = tokens.slice(0, -1)
  }
  return tokens.join(' ')
}

/** True when the transcript carries no usable content. */
export function isEmptyAnswer(input: string): boolean {
  return normalize(input).length === 0
}
